// src/pages/ELearning/TestRunner.tsx — DESIGN v2
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getTestById, getQuestions, submitResult } from '../../api/client';
import { Loader2, Clock, AlertCircle, ChevronLeft, ChevronRight, Check, CheckCircle2, Flag } from 'lucide-react';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';

interface Question {
  _id: string;
  questionText: string;
  questionType: 'single' | 'multiple' | 'text';
  options: { text: string; isCorrect: boolean }[];
  points: number;
}

interface Test {
  _id: string;
  title: string;
  timeLimit: number;
}

const TestRunner: React.FC = () => {
  const { testId } = useParams<{ testId: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { refreshUser } = useAuth();
  const [test, setTest] = useState<Test | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [answers, setAnswers] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [startTime] = useState(new Date());
  const [currentIdx, setCurrentIdx] = useState(0);
  // ─── ФИЧА P: Таймер ───
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null);
  const [timeIsUp, setTimeIsUp] = useState(false);

  // ─── ФИЧА P: Таймер — обратный отсчёт ───
  useEffect(() => {
    if (!test?.timeLimit || timeRemaining !== null) return;
    setTimeRemaining(test.timeLimit * 60); // секунды
  }, [test]);

  useEffect(() => {
    if (timeRemaining === null) return;
    if (timeRemaining <= 0) {
      setTimeIsUp(true);
      // Авто-сдача
      if (!submitting) {
        handleSubmit(true);
      }
      return;
    }
    const t = setTimeout(() => setTimeRemaining(timeRemaining - 1), 1000);
    return () => clearTimeout(t);
  }, [timeRemaining]);

  useEffect(() => {
    const fetchTest = async () => {
      try {
        const [testData, questionsData] = await Promise.all([
          getTestById(testId!),
          getQuestions(testId!),
        ]);
        setTest(testData);
        setQuestions(questionsData);
      } catch (err: any) {
        console.error('Ошибка загрузки теста:', err);
        setError(err.response?.data?.error || 'Не удалось загрузить тест');
      } finally {
        setLoading(false);
      }
    };
    fetchTest();
  }, [testId]);

  const handleAnswerChange = (questionId: string, value: any) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const isAnswered = (qId: string): boolean => {
    const ans = answers[qId];
    if (ans === undefined || ans === null) return false;
    if (Array.isArray(ans)) return ans.length > 0;
    if (typeof ans === 'string') return ans.trim() !== '';
    return true;
  };

  const answeredCount = questions.filter(q => isAnswered(q._id)).length;
  const progress = questions.length > 0 ? (answeredCount / questions.length) * 100 : 0;

  const currentQ = questions[currentIdx];

  const goPrev = () => {
    if (currentIdx > 0) setCurrentIdx(currentIdx - 1);
  };
  const goNext = () => {
    if (currentIdx < questions.length - 1) setCurrentIdx(currentIdx + 1);
  };

  const handleSubmit = async (autoSubmit = false) => {
    if (!test) return;

    if (!autoSubmit) {
      const unanswered = questions.filter(q => !isAnswered(q._id));
      if (unanswered.length > 0) {
        showToast(`Осталось ${unanswered.length} вопросов без ответа`, 'error');
        return;
      }
    } else {
      showToast('⏰ Время вышло! Тест автоматически отправлен', 'error');
    }

    setSubmitting(true);
    try {
      const payload = {
        testId: test._id,
        answers: Object.entries(answers).map(([questionId, value]) => ({
          questionId,
          selectedOptions: Array.isArray(value) ? value : [value],
          textAnswer: typeof value === 'string' && !Array.isArray(value) ? value : undefined,
        })),
        startedAt: startTime.toISOString(),
        completedAt: new Date().toISOString(),
      };
      const res = await submitResult(payload);
      await refreshUser();
      showToast(`Тест завершён! Результат: ${res.result.score.toFixed(1)}%`, 'success');
      navigate(`/app/elearning/tests/${testId}/result`, { state: { result: res } });
    } catch (err: any) {
      console.error('Ошибка отправки:', err);
      showToast(err.response?.data?.error || 'Не удалось отправить результаты', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <>
        <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}>
          <Loader2 size={40} className="spin" color="#667eea" />
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <div style={{ textAlign: 'center', padding: 60 }}>
          <AlertCircle size={48} color="#ef4444" />
          <h2 style={{ color: '#1a1a2e' }}>{error}</h2>
          <button onClick={() => navigate('/app/elearning/tests')} style={styles.retryButton}>
            Вернуться к тестам
          </button>
        </div>
      </>
    );
  }

  if (!test || questions.length === 0) {
    return (
      <>
        <div style={{ textAlign: 'center', padding: 60 }}>
          <AlertCircle size={48} color="#ef4444" />
          <h2 style={{ color: '#1a1a2e' }}>Тест не найден</h2>
        </div>
      </>
    );
  }

  const q = currentQ;
  const isFirst = currentIdx === 0;
  const isLast = currentIdx === questions.length - 1;

  return (
    <>
      <style>{`
        @keyframes fadeUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes slideIn { from { opacity: 0; transform: translateX(20px); } to { opacity: 1; transform: translateX(0); } }
        @keyframes pop { 0% { transform: scale(0.8); } 60% { transform: scale(1.1); } 100% { transform: scale(1); } }
        .tr-hero { animation: fadeUp 0.5s ease-out; }
        .tr-question { animation: slideIn 0.4s ease-out; }
        .tr-opt { transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1); cursor: pointer; }
        .tr-opt:hover { transform: translateY(-2px); box-shadow: 0 8px 20px rgba(102, 126, 234, 0.15) !important; border-color: rgba(102, 126, 234, 0.4) !important; }
        .tr-opt-active { border-color: transparent !important; background: linear-gradient(135deg, rgba(102, 126, 234, 0.08) 0%, rgba(118, 75, 162, 0.08) 100%) !important; box-shadow: 0 8px 24px rgba(102, 126, 234, 0.25) !important; }
        .tr-opt-active .tr-opt-check { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important; border-color: transparent !important; }
        .tr-opt-active .tr-opt-check-icon { opacity: 1 !important; }
        .tr-opt-check { transition: all 0.25s ease; }
        .tr-opt-check-icon { opacity: 0; transition: opacity 0.2s ease; animation: pop 0.3s ease; }
        .tr-nav-dot { transition: all 0.25s ease; cursor: pointer; }
        .tr-nav-dot:hover { transform: scale(1.15); }
        .tr-btn-primary { transition: all 0.3s ease; }
        .tr-btn-primary:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 12px 32px rgba(102, 126, 234, 0.4) !important; }
        .tr-btn-ghost { transition: all 0.25s ease; }
        .tr-btn-ghost:hover:not(:disabled) { background: rgba(102, 126, 234, 0.08) !important; border-color: rgba(102, 126, 234, 0.4) !important; }
        .tr-progress-fill { transition: width 0.5s cubic-bezier(0.4, 0, 0.2, 1); }
      `}</style>

      <div style={styles.container}>
        {/* ═══ Sticky top bar ═══ */}
        <div className="tr-hero" style={styles.topBar}>
          <div style={styles.topBarLeft}>
            <h1 style={styles.testTitle}>{test.title}</h1>
            <div style={styles.topBarMeta}>
              <span style={styles.metaChip}>
                <Clock size={13} /> {test.timeLimit} мин
              </span>
              <span style={styles.metaChipBlue}>
                <Flag size={13} /> {answeredCount} / {questions.length}
              </span>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            {timeRemaining !== null && (
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '10px 16px', borderRadius: 14, color: '#fff',
                fontWeight: 800, fontSize: 16, minWidth: 80, justifyContent: 'center',
                boxShadow: '0 6px 20px rgba(0,0,0,0.15)',
                background: timeRemaining <= 60
                  ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
                  : timeRemaining <= 300
                  ? 'linear-gradient(135deg, #f59e0b 0%, #f97316 100%)'
                  : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              }}>
                <Clock size={16} color="#fff" />
                <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {Math.floor(timeRemaining / 60)}:{String(timeRemaining % 60).padStart(2, '0')}
                </span>
              </div>
            )}
            <div style={styles.counterBlock}>
              <div style={styles.counterNumber}>
                {currentIdx + 1}<span style={styles.counterSlash}>/</span>{questions.length}
              </div>
              <div style={styles.counterLabel}>вопрос</div>
            </div>
          </div>
        </div>

        {/* ═══ Progress bar ═══ */}
        <div style={styles.progressTrack}>
          <div className="tr-progress-fill" style={{ ...styles.progressFill, width: `${progress}%` }} />
        </div>

        {/* ═══ Question card ═══ */}
        <div key={q._id} className="tr-question" style={styles.questionCard}>
          <div style={styles.qHeader}>
            <div style={styles.qBadge}>Вопрос {currentIdx + 1}</div>
            <div style={styles.qPoints}>
              {q.points} {q.points === 1 ? 'балл' : (q.points < 5 ? 'балла' : 'баллов')}
            </div>
          </div>

          <h2 style={styles.questionText}>{q.questionText}</h2>

          {q.questionType === 'multiple' && (
            <p style={styles.hintText}>💡 Можно выбрать несколько вариантов</p>
          )}

          {q.questionType === 'text' ? (
            <input
              type="text"
              placeholder="Введите ваш ответ..."
              value={answers[q._id] || ''}
              onChange={e => handleAnswerChange(q._id, e.target.value)}
              style={styles.textInput}
              autoFocus
            />
          ) : (
            <div style={styles.options}>
              {q.options.map((opt, i) => {
                const isSingle = q.questionType === 'single';
                const checked = isSingle
                  ? answers[q._id] === opt.text
                  : (answers[q._id] || []).includes(opt.text);

                return (
                  <div
                    key={i}
                    className={`tr-opt ${checked ? 'tr-opt-active' : ''}`}
                    onClick={() => {
                      if (isSingle) {
                        handleAnswerChange(q._id, opt.text);
                      } else {
                        const current = answers[q._id] || [];
                        if (checked) {
                          handleAnswerChange(q._id, current.filter((v: string) => v !== opt.text));
                        } else {
                          handleAnswerChange(q._id, [...current, opt.text]);
                        }
                      }
                    }}
                    style={styles.option}
                  >
                    <div className={`tr-opt-check`} style={styles.optCheck}>
                      <Check size={14} color="#fff" className="tr-opt-check-icon" strokeWidth={3} />
                    </div>
                    <span style={styles.optText}>{opt.text}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ═══ Navigator dots ═══ */}
        {questions.length > 1 && questions.length <= 30 && (
          <div style={styles.navigator}>
            {questions.map((qq, i) => {
              const done = isAnswered(qq._id);
              const isCurrent = i === currentIdx;
              return (
                <div
                  key={qq._id}
                  className="tr-nav-dot"
                  onClick={() => setCurrentIdx(i)}
                  style={{
                    ...styles.navDot,
                    background: isCurrent
                      ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                      : done
                      ? 'rgba(16, 185, 129, 0.6)'
                      : 'rgba(0,0,0,0.08)',
                    width: isCurrent ? 28 : 10,
                    boxShadow: isCurrent ? '0 4px 12px rgba(102, 126, 234, 0.4)' : 'none',
                  }}
                  title={`Вопрос ${i + 1}${done ? ' (отвечен)' : ''}`}
                />
              );
            })}
          </div>
        )}

        {/* ═══ Navigation buttons ═══ */}
        <div style={styles.navButtons}>
          <button
            onClick={goPrev}
            disabled={isFirst}
            className="tr-btn-ghost"
            style={{
              ...styles.ghostButton,
              opacity: isFirst ? 0.4 : 1,
              cursor: isFirst ? 'not-allowed' : 'pointer',
            }}
          >
            <ChevronLeft size={18} /> Назад
          </button>

          {!isLast ? (
            <button
              onClick={goNext}
              className="tr-btn-primary"
              style={styles.primaryButton}
            >
              Далее <ChevronRight size={18} />
            </button>
          ) : (
            <button
              onClick={() => handleSubmit()}
              disabled={submitting || answeredCount < questions.length}
              className="tr-btn-primary"
              style={{
                ...styles.submitButton,
                opacity: submitting || answeredCount < questions.length ? 0.6 : 1,
                cursor: submitting || answeredCount < questions.length ? 'not-allowed' : 'pointer',
              }}
              title={answeredCount < questions.length ? 'Ответьте на все вопросы' : 'Завершить тест'}
            >
              {submitting ? (
                <><Loader2 size={18} className="spin" /> Отправка...</>
              ) : (
                <><CheckCircle2 size={18} /> Завершить тест</>
              )}
            </button>
          )}
        </div>

        {/* ═══ Submit button (always visible if all answered, even not on last) ═══ */}
        {!isLast && answeredCount === questions.length && (
          <div style={styles.quickSubmit}>
            <button
              onClick={() => handleSubmit()}
              disabled={submitting}
              className="tr-btn-primary"
              style={styles.submitButton}
            >
              {submitting ? (
                <><Loader2 size={18} className="spin" /> Отправка...</>
              ) : (
                <><CheckCircle2 size={18} /> Все готово — Завершить тест</>
              )}
            </button>
          </div>
        )}
      </div>
    </>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: { maxWidth: 900, margin: '0 auto', paddingBottom: 60 },

  topBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 16,
    marginBottom: 16,
    flexWrap: 'wrap',
  },
  topBarLeft: { flex: 1, minWidth: 200 },
  testTitle: {
    fontSize: 24,
    fontWeight: 800,
    margin: 0,
    marginBottom: 8,
    color: '#1a1a2e',
    letterSpacing: '-0.5px',
    lineHeight: 1.2,
  },
  topBarMeta: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  metaChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    padding: '5px 10px',
    background: 'rgba(245, 158, 11, 0.12)',
    color: '#d97706',
    borderRadius: 10,
    fontSize: 12,
    fontWeight: 700,
  },
  metaChipBlue: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    padding: '5px 10px',
    background: 'rgba(102, 126, 234, 0.12)',
    color: '#667eea',
    borderRadius: 10,
    fontSize: 12,
    fontWeight: 700,
  },
  counterBlock: {
    textAlign: 'right',
    flexShrink: 0,
  },
  counterNumber: {
    fontSize: 32,
    fontWeight: 800,
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    lineHeight: 1,
    letterSpacing: '-1px',
  },
  counterSlash: { opacity: 0.3, margin: '0 2px' },
  counterLabel: {
    fontSize: 10,
    color: '#8b8fa8',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 1,
    marginTop: 4,
  },

  progressTrack: {
    height: 6,
    background: 'rgba(102, 126, 234, 0.1)',
    borderRadius: 3,
    overflow: 'hidden',
    marginBottom: 28,
  },
  progressFill: {
    height: '100%',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    borderRadius: 3,
    minWidth: 4,
  },

  questionCard: {
    background: 'rgba(255, 255, 255, 0.55)',
    backdropFilter: 'blur(24px) saturate(180%)',
    WebkitBackdropFilter: 'blur(24px) saturate(180%)',
    border: '1px solid rgba(255, 255, 255, 0.7)',
    borderRadius: 28,
    padding: 32,
    boxShadow: '0 8px 32px rgba(31, 38, 135, 0.08)',
    marginBottom: 24,
  },
  qHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
    gap: 12,
  },
  qBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '5px 12px',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: '#fff',
    borderRadius: 10,
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
    boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
  },
  qPoints: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '5px 12px',
    background: 'linear-gradient(135deg, #f6d365 0%, #fda085 100%)',
    color: '#fff',
    borderRadius: 10,
    fontSize: 12,
    fontWeight: 800,
    boxShadow: '0 4px 12px rgba(246, 211, 101, 0.4)',
  },
  questionText: {
    fontSize: 20,
    fontWeight: 700,
    color: '#1a1a2e',
    margin: '0 0 8px',
    lineHeight: 1.4,
  },
  hintText: {
    fontSize: 13,
    color: '#8b8fa8',
    margin: '0 0 20px',
    fontStyle: 'italic',
  },
  options: { display: 'flex', flexDirection: 'column', gap: 10, marginTop: 20 },
  option: {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    padding: '16px 20px',
    background: 'rgba(255, 255, 255, 0.6)',
    border: '2px solid rgba(0,0,0,0.06)',
    borderRadius: 16,
    boxShadow: '0 2px 8px rgba(31, 38, 135, 0.04)',
  },
  optCheck: {
    width: 24,
    height: 24,
    borderRadius: 8,
    border: '2px solid rgba(0,0,0,0.15)',
    background: 'rgba(255,255,255,0.8)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  optText: { fontSize: 15, fontWeight: 500, color: '#1a1a2e', lineHeight: 1.4, flex: 1 },
  textInput: {
    width: '100%',
    padding: '16px 20px',
    borderRadius: 16,
    border: '2px solid rgba(0,0,0,0.08)',
    background: 'rgba(255,255,255,0.8)',
    fontSize: 15,
    outline: 'none',
    marginTop: 20,
    boxSizing: 'border-box',
    fontFamily: 'inherit',
    transition: 'all 0.2s',
  },

  navigator: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 6,
    flexWrap: 'wrap',
    marginBottom: 24,
    padding: '12px 16px',
    background: 'rgba(255, 255, 255, 0.5)',
    backdropFilter: 'blur(16px)',
    WebkitBackdropFilter: 'blur(16px)',
    borderRadius: 20,
    border: '1px solid rgba(255, 255, 255, 0.6)',
  },
  navDot: {
    height: 10,
    borderRadius: 5,
    background: 'rgba(0,0,0,0.08)',
  },

  navButtons: {
    display: 'flex',
    gap: 12,
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  ghostButton: {
    padding: '14px 24px',
    background: 'rgba(255, 255, 255, 0.7)',
    border: '1px solid rgba(0,0,0,0.08)',
    borderRadius: 16,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    fontWeight: 700,
    fontSize: 15,
    color: '#5a5f7a',
    cursor: 'pointer',
  },
  primaryButton: {
    padding: '14px 28px',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: '#fff',
    border: 'none',
    borderRadius: 16,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    fontWeight: 700,
    fontSize: 15,
    cursor: 'pointer',
    boxShadow: '0 8px 24px rgba(102, 126, 234, 0.35)',
  },
  submitButton: {
    padding: '14px 28px',
    background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
    color: '#fff',
    border: 'none',
    borderRadius: 16,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    fontWeight: 700,
    fontSize: 15,
    cursor: 'pointer',
    boxShadow: '0 8px 24px rgba(17, 153, 142, 0.35)',
  },
  quickSubmit: {
    marginTop: 20,
    textAlign: 'center',
    animation: 'fadeUp 0.4s ease-out',
  },

  retryButton: {
    marginTop: 20,
    padding: '10px 24px',
    background: '#2575fc',
    color: 'white',
    border: 'none',
    borderRadius: 30,
    cursor: 'pointer',
    fontWeight: 600,
  },
};

export default TestRunner;
